import { createContext, type ReactNode, useContext } from "react";
import { Column, Heading } from "@hightouch/htui";
import { EntityHeaderSection } from "src/pages/ad-studio/insights/entity-header-section";
import { ListDrilldownTable } from "src/pages/ad-studio/insights/list-drilldown-table";
import { ListTable } from "src/pages/ad-studio/insights/list-table";
import { FailureAlert } from "src/pages/ad-studio/insights/performance/failure-alert";
import type {
  DrillChildrenAction,
  EntityDrilldownParentAction,
  EntityDrilldownState,
} from "src/pages/ad-studio/insights/performance/types";
import { PerformanceTableSkeleton } from "src/pages/ad-studio/insights/performance/view/performance-table-skeleton";
import { PerformanceViewPrimitive } from "src/pages/ad-studio/insights/performance/view/performance-view-primitive";
import type {
  EntityKind,
  Page,
} from "src/pages/ad-studio/insights/performance/view/types";
import { toOpenedRow } from "src/pages/ad-studio/insights/shared/drilldown-state";

type EntityDrilldownContextValue = EntityDrilldownState & {
  performAction: (action: DrillChildrenAction) => void;
};

const EntityDrilldownContext = createContext<
  EntityDrilldownContextValue | undefined
>(undefined);

const useEntityDrilldownContext = (part: string) => {
  const context = useContext(EntityDrilldownContext);
  if (context === undefined) {
    throw new Error(
      `\`EntityDrilldownPrimitive.${part}\` must be used within \`EntityDrilldownPrimitive.Root\``,
    );
  }
  return context;
};

type RootProps = EntityDrilldownContextValue & { children?: ReactNode };

const Root = ({
  header,
  notFoundHeading,
  childList,
  performAction,
  children,
}: RootProps) => (
  <EntityDrilldownContext.Provider
    value={{ header, notFoundHeading, childList, performAction }}
  >
    <Column gap={6}>{children}</Column>
  </EntityDrilldownContext.Provider>
);

const Header = () => {
  const { header, notFoundHeading } = useEntityDrilldownContext("Header");
  return (
    <EntityHeaderSection header={header} notFoundHeading={notFoundHeading} />
  );
};

const ChildPage = ({
  page,
  performAction,
  children,
}: {
  page: Page<EntityKind>;
  performAction: (action: DrillChildrenAction) => void;
  children?: ReactNode;
}) => (
  <PerformanceViewPrimitive.Root
    paging={page.paging}
    performAction={(action) =>
      performAction({ kind: "DrillChildrenAction/paged", page: action.page })
    }
  >
    <PerformanceViewPrimitive.TableLayout>
      {children}
      <PerformanceViewPrimitive.Pagination />
    </PerformanceViewPrimitive.TableLayout>
  </PerformanceViewPrimitive.Root>
);

const PlainChildList = ({
  childList,
  performAction,
}: Pick<EntityDrilldownContextValue, "childList" | "performAction">) => {
  const { list } = childList;
  switch (list.status) {
    case "loading": {
      return <PerformanceTableSkeleton columns={list.planned} />;
    }
    case "failed": {
      return <FailureAlert failure={list.failure} />;
    }
    case "loaded": {
      return (
        <ChildPage page={list.page} performAction={performAction}>
          <ListTable
            page={list.page}
            performAction={(action) =>
              performAction({
                kind: "DrillChildrenAction/sorted-column",
                key: action.key,
              })
            }
          />
        </ChildPage>
      );
    }
    default: {
      return list satisfies never;
    }
  }
};

const DrilldownChildList = ({
  childList,
  performAction,
  performParentAction,
}: Pick<EntityDrilldownContextValue, "childList" | "performAction"> & {
  performParentAction: (action: EntityDrilldownParentAction) => void;
}) => {
  const { list } = childList;
  switch (list.status) {
    case "loading": {
      return <PerformanceTableSkeleton columns={list.planned} />;
    }
    case "failed": {
      return <FailureAlert failure={list.failure} />;
    }
    case "loaded": {
      return (
        <ChildPage page={list.page} performAction={performAction}>
          <ListDrilldownTable
            page={list.page}
            performAction={(action) => {
              switch (action.kind) {
                case "ListDrilldownTableAction/drilled-down": {
                  return performParentAction({
                    kind: "EntityDrilldownParentAction/opened-row",
                    ...toOpenedRow(action.opened),
                  });
                }
                case "PerformanceViewAction/sorted-column": {
                  return performAction({
                    kind: "DrillChildrenAction/sorted-column",
                    key: action.key,
                  });
                }
                default: {
                  return action satisfies never;
                }
              }
            }}
          />
        </ChildPage>
      );
    }
    default: {
      return list satisfies never;
    }
  }
};

/** The child list with rows that only show their cells, for surfaces where nothing can open them. */
const Children = () => {
  const { childList, performAction } = useEntityDrilldownContext("Children");
  return (
    <Column gap={2}>
      <Heading size="md">{childList.heading}</Heading>
      <PlainChildList childList={childList} performAction={performAction} />
    </Column>
  );
};

/** The child list with rows that drill down into their own entity. */
const DrilldownChildren = ({
  performParentAction,
}: {
  performParentAction: (action: EntityDrilldownParentAction) => void;
}) => {
  const { childList, performAction } =
    useEntityDrilldownContext("DrilldownChildren");
  return (
    <Column gap={2}>
      <Heading size="md">{childList.heading}</Heading>
      <DrilldownChildList
        childList={childList}
        performAction={performAction}
        performParentAction={performParentAction}
      />
    </Column>
  );
};

export const EntityDrilldownPrimitive = {
  Root,
  Header,
  Children,
  DrilldownChildren,
};
